<template>
  <section>
    <div v-if="loading">
      <div class="pb-1 pl-1">
        <b-skeleton
          type="button"
          width="140px"
          height="34px"
        />
      </div>
      <b-skeleton-table
        :rows="10"
        :columns="fields.length"
      />
    </div>
    <template v-if="!loading">
      <b-button
        variant="primary"
        class="mb-1 ml-1"
        :to="'catalogs/create'"
        size="sm"
      >

        <GjIcon
          class="mr-50"
          name="Plus"
        />
        {{ $tk('ClientPortal.Catalogs.CreateCatalog') }}
      </b-button>
      <b-card
        no-body
        footer-class="d-flex"
        class="client-table--catalogs"
      >
        <b-table
          :items="catalogs"
          :fields="fields"
          class="client-table__data catalog-list-table"
          show-empty
          :tbody-tr-class="newRecordRowStyle"
          :empty-text="$tk('ClientPortal.Catalogs.NoCatalogsHaveBeenCreatedYet')"
          :current-page="currentPage"
          :per-page="perPage"
          responsive
        >
          <template #empty="scope">
            <div class="text-center p-3 client-table__empty">
              <div class="empty__client-table">
                <GjIcon
                  size="64px"
                  name="Book"
                />
                <div class="mb-50">
                  {{ scope.emptyText }}
                </div>
                <b-link :to="'catalogs/create'">
                  <GjIcon
                    size="14"
                    name="Plus"
                  />
                  <span class="create-text"> {{ $tk('ClientPortal.Catalogs.CreateCatalog') }}</span>
                </b-link>
              </div>
            </div>
          </template>
          <template #cell(name)="data">
            <i
              v-b-tooltip.hover="data.item.status"
              class="mr-50"
              :class="getCatalogStatusIcon(data.item.status)"
            />
            <b-link :to="'/' + $route.params.accountId +'/catalogs/' + data.item.encodedId + '/details'">
              {{ data.item.name }}
            </b-link>
          </template>
          <template #cell(catalogItemsCount)="data">
            {{ data.item.catalogItemsCount | formatInteger }}
          </template>
          <template #cell(lastCheckDateTime)="data">
            <div class="d-flex align-items-center justify-content-between">
              <span>
                {{ data.item.lastCheckDateTime }}
              </span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
                button-class="p-0"
                right
              >
                <template v-slot:button-content>
                  <GjIcon name="Moreoptions_fill" />
                </template>
                <b-dropdown-item
                  :to="'/' + $route.params.accountId +'/catalogs/' + data.item.encodedId + '/details'"
                >
                  <GjIcon
                    class="mr-50"
                    name="Edit"
                  />
                  {{ $tk('ClientPortal.Form.Edit') }}
                </b-dropdown-item>
                <b-dropdown-item @click="showDeleteConfirmModal(data.item)">
                  <GjIcon
                    class="mr-50"
                    name="Delete"
                  />
                  {{ $tk('ClientPortal.Form.Delete') }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>
        <b-card-footer
          v-if="catalogs.length > 0"
          class="client-table__footer d-flex"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            class="my-0 mr-1"
            hide-goto-end-buttons
          >
            <template #prev-text>
              <GjIcon
                class="mr-25"
                name="ArrowLeft"
              />
            </template>
            <template #next-text>
              <GjIcon
                class="ml-25"
                name="ArrowRight"
              />
            </template>
          </b-pagination>
          <span>
            {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }} {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }} {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }} {{ $tk('ClientPortal.Pagination.Items') }}
          </span>
        </b-card-footer>
      </b-card>
    </template>

    <b-modal
      ref="deleteModal"
      :title="$tk('ClientPortal.Catalogs.AreYouSureYouWantToDeleteThisCatalog')"
      centered
    >
      <p>{{ $tk('ClientPortal.Catalogs.Name') }}: <b>{{ deleteConfirmModel.catalog.name }}</b></p>
      <p>{{ $tk('ClientPortal.Catalogs.PropertyName') }}: <b>{{ deleteConfirmModel.catalog.propertyName }}</b></p>
      <p class="mb-0">
        {{ $tk('ClientPortal.Catalogs.CatalogUrl') }}: <b>{{ deleteConfirmModel.catalog.catalogUrl }}</b></p>
      <template
        v-slot:modal-footer="{ cancel }"
      >
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="deleteConfirmModel.loading"
          size="sm"
          @click="deleteCatalog(deleteConfirmModel.catalog)"
        >
          {{ $tk('ClientPortal.MasterTable.ConfirmDialog.Delete') }}
          <b-spinner
            v-if="deleteConfirmModel.loading"
            class="ml-50"
            variant="light"
            label="Spinning"
            small
          />
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import CatalogsService from '@/api/client/catalogs.service'
import notification from '@/mixins/toastification'
import { computed, ref } from '@vue/composition-api'
import store from '@/store'

export default {
  components: {
    GjIcon,
  },
  mixins: [notification],
  data() {
    return {
      catalogs: [],
      currentPage: 1,
      perPage: 10,
      loading: true,
      deleteConfirmModel: {
        catalog: {
          name: '',
          propertyName: '',
          catalogUrl: '',
        },
        loading: false,
      },
    }
  },
  computed: {
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    fields() {
      return [
        {
          key: 'name',
          label: this.$tk('ClientPortal.Catalogs.Name'),
        },
        {
          key: 'propertyName',
          label: this.$tk('ClientPortal.Catalogs.PropertyName'),
        },
        {
          key: 'type',
          label: this.$tk('ClientPortal.Catalogs.Type'),
        },
        {
          key: 'catalogItemsCount',
          label: this.$tk('ClientPortal.Catalogs.CatalogItemsCount'),
        },
        {
          key: 'catalogAdvertisingPrivacyType',
          label: this.$tk('ClientPortal.Catalogs.CatalogAdvertisingPrivacyType'),
        },
        {
          key: 'catalogTargetingType',
          label: this.$tk('ClientPortal.Catalogs.CatalogTargetingType'),
        },
        {
          key: 'lastCheckDateTime',
          label: this.$tk('ClientPortal.Catalogs.LastFetchDateTime'),
        },
      ]
    },
    totalRows() {
      return this.catalogs.length
    },
  },
  watch: {
    '$route.params.accountId': 'fetchData',
  },
  async mounted() {
    this.loading = true
    try {
      const catalogs = await CatalogsService.getCatalogs(this.$route.params.accountId)
      this.catalogs = catalogs.reverse()
    } catch {
      this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
    }
    this.loading = false
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const catalogs = await CatalogsService.getCatalogs(this.$route.params.accountId)
        this.catalogs = catalogs.reverse()
      } catch {
        this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
      } finally {
        this.loading = false
      }
    },
    showDeleteConfirmModal(catalog) {
      this.deleteConfirmModel.catalog = catalog
      this.$refs.deleteModal.show()
    },
    async deleteCatalog(catalog) {
      try {
        this.deleteConfirmModel.loading = true
        const response = await CatalogsService.deleteCatalog(catalog.encodedId, this.$route.params.accountId)
        if (response.saved) {
          this.catalogs = this.catalogs.filter(x => x.encodedId !== catalog.encodedId)
          this.success(response.message)
        } else {
          this.error(response.message)
        }
      } catch (err) {
        this.error(err.message)
      } finally {
        this.deleteConfirmModel.loading = false
        this.$refs.deleteModal.hide()
      }
    },
  },
  setup() {
    const isCreatedNew = computed(() => store.state.app.isCreatedNew)
    const alreadySet = ref(false)
    const firstRow = ref(null)

    const newRecordRowStyle = () => {
      if (!alreadySet.value) {
        firstRow.value = Array.from(document.querySelectorAll('.catalog-list-table > table > tbody > tr:first-child')).pop()
        if (firstRow.value) {
          if (isCreatedNew.value) {
            firstRow.value.classList.add('table-row-animation')
          } else if (firstRow.value.classList.contains('table-row-animation')) {
            firstRow.value.classList.remove('table-row-animation')
          }
          alreadySet.value = true
        }
      }
      return ''
    }

    const getCatalogStatusIcon = status => {
      switch (status) {
        case 'Pending':
          return 'chart-dot-blue'
        case 'Processing':
          return 'chart-dot-orange'
        case 'Finished':
          return 'chart-dot-green'
        default:
          return 'chart-dot-blue'
      }
    }

    if (isCreatedNew.value) {
      setTimeout(() => {
        store.commit('app/TOGGLE_NEW_CREATE_STATE', false)
        firstRow.value.classList.remove('table-row-animation')
      }, 3000)
    }
    return {
      isCreatedNew,
      newRecordRowStyle,
      getCatalogStatusIcon,
    }
  },
}
</script>
<style lang="scss">
@import '@/assets/scss/views/added_row_animation.scss';
</style>
